import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

type AdminUnsavedContextType = {
  isDirty: boolean;
  setDirty: (dirty: boolean) => void;
  /** Item já publicado com edições não salvas (modal com Salvar e sair / Sair / Cancelar) */
  isPublishedLeave: boolean;
  setIsPublishedLeave: (v: boolean) => void;
  /**
   * Quando true, não grava rascunho automaticamente ao mudar de rota (ex.: notícias em rascunho com status programado).
   * Usa-se o modal de três vias com `runSaveDraft`.
   */
  skipAutoFlushOnLeave: boolean;
  setSkipAutoFlushOnLeave: (v: boolean) => void;
  setFlushDraftHandler: (fn: (() => Promise<boolean>) | null) => void;
  setSavePublishedHandler: (fn: (() => Promise<boolean>) | null) => void;
  /** Salvar rascunho completo (incl. status programado) ao sair — só quando registado. */
  setSaveDraftHandler: (fn: (() => Promise<boolean>) | null) => void;
  runFlushDraft: () => Promise<boolean>;
  runSavePublished: () => Promise<boolean>;
  runSaveDraft: () => Promise<boolean>;
};

const AdminUnsavedContext = createContext<AdminUnsavedContextType | null>(null);

export function AdminUnsavedProvider({ children }: { children: React.ReactNode }) {
  const [isDirty, setIsDirtyState] = useState(false);
  const [isPublishedLeave, setIsPublishedLeave] = useState(false);
  const [skipAutoFlushOnLeave, setSkipAutoFlushOnLeave] = useState(false);
  const flushDraftRef = useRef<(() => Promise<boolean>) | null>(null);
  const savePublishedRef = useRef<(() => Promise<boolean>) | null>(null);
  const saveDraftRef = useRef<(() => Promise<boolean>) | null>(null);

  const setDirty = useCallback((dirty: boolean) => setIsDirtyState(dirty), []);

  const setFlushDraftHandler = useCallback((fn: (() => Promise<boolean>) | null) => {
    flushDraftRef.current = fn;
  }, []);

  const setSavePublishedHandler = useCallback((fn: (() => Promise<boolean>) | null) => {
    savePublishedRef.current = fn;
  }, []);

  const setSaveDraftHandler = useCallback((fn: (() => Promise<boolean>) | null) => {
    saveDraftRef.current = fn;
  }, []);

  const runFlushDraft = useCallback(async () => {
    if (!flushDraftRef.current) return false;
    return flushDraftRef.current();
  }, []);

  const runSavePublished = useCallback(async () => {
    if (!savePublishedRef.current) return false;
    return savePublishedRef.current();
  }, []);

  const runSaveDraft = useCallback(async () => {
    if (!saveDraftRef.current) return false;
    return saveDraftRef.current();
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  return (
    <AdminUnsavedContext.Provider
      value={{
        isDirty,
        setDirty,
        isPublishedLeave,
        setIsPublishedLeave,
        skipAutoFlushOnLeave,
        setSkipAutoFlushOnLeave,
        setFlushDraftHandler,
        setSavePublishedHandler,
        setSaveDraftHandler,
        runFlushDraft,
        runSavePublished,
        runSaveDraft,
      }}
    >
      {children}
    </AdminUnsavedContext.Provider>
  );
}

export function useAdminUnsaved() {
  const ctx = useContext(AdminUnsavedContext);
  return (
    ctx ?? {
      isDirty: false,
      setDirty: () => {},
      isPublishedLeave: false,
      setIsPublishedLeave: () => {},
      skipAutoFlushOnLeave: false,
      setSkipAutoFlushOnLeave: () => {},
      setFlushDraftHandler: () => {},
      setSavePublishedHandler: () => {},
      setSaveDraftHandler: () => {},
      runFlushDraft: async () => false,
      runSavePublished: async () => false,
      runSaveDraft: async () => false,
    }
  );
}
